import classNames from 'classnames';
import React, {
  InputHTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Input from './Input';
import { DayPicker } from 'react-day-picker';

import './DatePicker.css';
import { CalendarDaysIcon } from 'lucide-react';
import { format } from 'date-fns';

interface DatePickerProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  className?: string;
  inputClassName?: string;
  error?: string | boolean;
  value: Date | undefined;
  label?: string | React.ReactElement;
  onChange?: (newDate: Date | undefined, name: string) => void;
  allowAfterToday?: boolean;
  closeOnSelect?: boolean;
}

const dateFormatter = Intl.DateTimeFormat('en', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
});

function DatePicker({
  className,
  value,
  onChange,
  onBlur,
  name,
  allowAfterToday = false,
  closeOnSelect = false,
  ...rest
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const datePickerRef = useRef<HTMLDivElement | null>(null); // Reference for DayPicker container
  const datePickerInputRef = useRef<HTMLLabelElement>(null); // Reference for DayPicker input

  const [selectedDate, setSelectedDateFn] = useState(value);
  const [selectedMonth, setSelectedMonth] = useState(value ?? new Date());

  const handleDayPickerSelect = (newDate: Date | undefined) => {
    setSelectedDateFn(newDate);
    if (onChange) {
      const formattedNewDate = newDate ? new Date(format(newDate, 'yyyy-MM-dd')) : undefined;
      onChange(formattedNewDate, name ?? '');
      if (closeOnSelect) {
        setIsOpen(false);
      }
    }
  };

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        datePickerRef.current &&
        datePickerInputRef.current &&
        !datePickerRef.current.contains(event.target as Node) &&
        !datePickerInputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false); // Close the date picker if click is outside
        if (onBlur) {
          onBlur(new Event('blur') as unknown as React.FocusEvent<HTMLInputElement>);
        }
        // setSelectedDate(new Date(rest.value));
      }
    },
    [onBlur]
  );

  // Handle clicking outside of the date picker to close it
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mouseup', handleClickOutside);
    } else {
      document.removeEventListener('mouseup', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
    };
  }, [handleClickOutside, isOpen]);

  const formattedValue = useMemo(() => {
    if (!value) {
      return '';
    }

    const v: string = value ? dateFormatter.formatRange(value, value) : '';
    return v;
  }, [value]);

  return (
    <div className={classNames('relative', className)}>
      <Input
        labelIcon={
          <span className="text-heading inline-block">
            <CalendarDaysIcon size={16} />
          </span>
        }
        onFocus={() => {
          setIsOpen(true);
        }}
        autoComplete="off"
        labelRef={datePickerInputRef}
        value={formattedValue}
        onChange={() => {
          if (onChange) {
            handleDayPickerSelect(undefined);
          }
        }}
        readOnly
        clearable
        focusOnClear={false}
        {...rest}
      />
      {isOpen && (
        <div
          ref={datePickerRef}
          className="absolute z-10 rounded-md border-2 border-neutral-500 bg-white px-3"
        >
          <DayPicker
            timeZone="UTC"
            captionLayout="dropdown"
            mode="single"
            selected={selectedDate}
            onMonthChange={(newMonth) => {
              setSelectedMonth(newMonth);
            }}
            disabled={allowAfterToday ? undefined : { after: new Date() }}
            fixedWeeks
            weekStartsOn={1}
            onSelect={handleDayPickerSelect}
            showOutsideDays
            required={false}
            month={selectedMonth}
          />
        </div>
      )}
    </div>
  );
}

export default DatePicker;
